import { useMemo } from 'react';
import { Formik, Form } from 'formik';
import { Box, Button } from '@rsos/base-components';
import MultiEntryInput from '@rsos/components/capstone/base/form/inputs/MultiEntryInput/MultiEntryInput';
import { useUpdateEventShareMutation } from '@rsos/reduxDataStore/src/features/eventShare/eventShareApi';
import { EventShare } from '@rsos/reduxDataStore/src/features/eventShare/types';
import { RecipientsRequest } from '@rsos/reduxDataStore/src/features/eventShare/types';
import { extractErrorMessage } from '@rsos/reduxDataStore/src/utils/errorUtils';
import { notifyError, notifySuccess } from '@rsos/utils/toastUtils';
import { isValidEmail } from '@rsos/utils/validators';
import { markEmailRecipientValidity } from '../../../../../../../utils/emailRecipientTags';
import {
  getValidationSchema,
  CustomEnvelopIcon,
  MultiInputTag,
  RecipientTag,
} from '../../../utils';
import FormTriggers from './FormTriggers';

interface AddRecipientsFormValues {
  recipients: MultiInputTag[];
  inputValue: string;
}

const AddRecipients = ({
  share,
  setIsEditing,
  existingRecipients,
}: {
  share: EventShare;
  setIsEditing: (isEditing: boolean) => void;
  existingRecipients: RecipientTag[];
}) => {
  const [updateEventShare] = useUpdateEventShareMutation();

  const validationSchema = useMemo(() => {
    return getValidationSchema(existingRecipients);
  }, [existingRecipients]);

  const handleAddRecipients = async (values: AddRecipientsFormValues) => {
    if (values.recipients.length === 0) return;

    try {
      const newRecipients: RecipientsRequest = {
        emails: [],
        phone_numbers: [],
        organization_contacts: [],
        contact_groups: [],
      };

      // Recipients are email-only — phone/SMS delivery was removed. Only valid
      // email addresses are sent; anything else is dropped and is also blocked
      // from submission by the validation schema.
      values.recipients.forEach(tag => {
        if (isValidEmail(tag.value)) {
          newRecipients.emails.push(tag.value);
        }
      });

      await updateEventShare({
        org: share.organization,
        sessionId: share.session_id,
        uri: share.uri,
        recipients: newRecipients,
      }).unwrap();

      notifySuccess('Recipients added successfully');
      setIsEditing(false);
    } catch (error: unknown) {
      const errorMessage = extractErrorMessage(
        error,
        'Failed to add recipients',
      );
      notifyError(errorMessage);
    }
  };

  return (
    <Box mt="12px">
      <Formik<AddRecipientsFormValues>
        initialValues={{ recipients: [], inputValue: '' }}
        validationSchema={validationSchema}
        onSubmit={handleAddRecipients}
      >
        {({ values, setFieldValue, errors, isValid }) => (
          <Form>
            <FormTriggers existingRecipients={existingRecipients} />
            <MultiEntryInput
              inputType="mixed"
              value={values.inputValue}
              name="inputValue"
              placeholder="Add an email recipient..."
              hiddenValue={values.recipients}
              hiddenInputName="recipients"
              // Email-only: flag non-email entries (phone numbers, malformed
              // addresses like "test@com") as invalid error tags. The validation
              // schema and submit handler independently enforce email-only too.
              tags={markEmailRecipientValidity(values.recipients)}
              updateTags={(tags: MultiInputTag[]) => {
                setFieldValue('recipients', tags);
              }}
              setFieldValue={setFieldValue}
              handleChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                setFieldValue('inputValue', e.target.value);
              }}
              validator={() => true}
              icons={{ email: CustomEnvelopIcon }}
              error={
                typeof errors.recipients === 'string'
                  ? errors.recipients
                  : undefined
              }
            />
            <Box horizontal mt="12px" justify="flex-end">
              <Button
                btnType="primary"
                size="small"
                type="submit"
                disabled={!isValid || values.recipients.length === 0}
              >
                Add Recipients
              </Button>
            </Box>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default AddRecipients;
